import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { FormulaApiState } from '../types/formulaApi.types';
import {
  createFormulaSession,
  deleteFormulaSession,
  generateFormula,
  reviewFormula,
} from './formulaApi.thunk';

const initialState: FormulaApiState = {
  sessionId: null,
  response: null,
  status: 'idle',
  error: null,
  currentRequestId: null,
};

const formulaApiSlice = createSlice({
  name: 'formulaApi',
  initialState,
  reducers: {
    resetFormulaApi: () => initialState,
    clearFormulaApiError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createFormulaSession.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.sessionId = action.payload;
        state.status = 'idle';
        state.currentRequestId = null;
      })
      .addCase(deleteFormulaSession.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        return initialState;
      })
      .addMatcher(
        isAnyOf(generateFormula.fulfilled, reviewFormula.fulfilled),
        (state, action) => {
          if (state.currentRequestId !== action.meta.requestId) return;
          state.response = action.payload;
          state.sessionId =
            action.payload.sessionId ?? action.meta.arg.sessionId;
          state.status = 'idle';
          state.currentRequestId = null;
        }
      )
      .addMatcher(
        isAnyOf(
          createFormulaSession.pending,
          generateFormula.pending,
          reviewFormula.pending,
          deleteFormulaSession.pending
        ),
        (state, action) => {
          state.status = createFormulaSession.pending.match(action)
            ? 'creating'
            : deleteFormulaSession.pending.match(action)
              ? 'deleting'
              : 'messaging';
          state.error = null;
          state.currentRequestId = action.meta.requestId;
        }
      )
      .addMatcher(
        isAnyOf(
          createFormulaSession.rejected,
          generateFormula.rejected,
          reviewFormula.rejected,
          deleteFormulaSession.rejected
        ),
        (state, action) => {
          if (state.currentRequestId !== action.meta.requestId) return;
          state.status = 'idle';
          state.currentRequestId = null;
          state.error = action.meta.aborted
            ? null
            : (action.payload ??
              'The FormulaMate AI request failed. Please try again.');
        }
      );
  },
});

export const { resetFormulaApi, clearFormulaApiError } =
  formulaApiSlice.actions;
export default formulaApiSlice.reducer;
